import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { LayoutGrid, Plus, RotateCcw, Save, X, Pencil, RefreshCw } from 'lucide-react';
import { WidgetCard, WidgetSkeleton } from '../components/dashboard/WidgetCard';
import { WidgetPicker } from '../components/dashboard/WidgetPicker';
import { WIDGET_COMPONENTS, WIDGET_MODULE_LINKS, badgeFor } from '../components/dashboard/registry';
import { useNavigate } from 'react-router-dom';
import CredentialSummaryCard from '../components/CredentialSummaryCard';

const SIZE_ORDER = ['small', 'medium', 'large'];

const roleLabel = (role) => {
  const known = {
    admin: 'System Admin', warehouse: 'Warehouse Staff', manager: 'Manager',
    supervisor: 'Supervisor', hr: 'HR', finance: 'Finance / Procurement',
    marketing: 'Marketing', fleet_manager: 'Fleet Manager', auditor: 'Auditor',
  };
  if (known[role]) return known[role];
  return String(role || '')
    .split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const today = () => new Date().toLocaleDateString(undefined, {
  weekday: 'long', month: 'long', day: 'numeric',
});

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [layout, setLayout] = useState([]);
  const [draft, setDraft] = useState(null);          // non-null while editing
  const [preferences, setPreferences] = useState({});
  const [payloads, setPayloads] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyKeys, setBusyKeys] = useState(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const fetchedAt = useRef({});
  const dragFrom = useRef(null);

  const editMode = draft !== null;
  const view = editMode ? draft : layout;
  const byKey = useMemo(
    () => Object.fromEntries(catalog.map((w) => [w.widget_key, w])), [catalog]);

  const fetchData = useCallback(async (keys, refresh = false) => {
    if (!keys.length) return;
    setBusyKeys((prev) => new Set([...prev, ...keys]));
    try {
      const res = await dashboardAPI.getData(keys, refresh);
      const now = Date.now();
      keys.forEach((k) => { fetchedAt.current[k] = now; });
      setPayloads((prev) => ({ ...prev, ...res.data.widgets }));
    } catch {
      setPayloads((prev) => ({
        ...prev,
        ...Object.fromEntries(keys.map((k) => [k, { status: 'error' }])),
      }));
    } finally {
      setBusyKeys((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.delete(k));
        return next;
      });
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, lay] = await Promise.all([
        dashboardAPI.getWidgets(), dashboardAPI.getLayout(),
      ]);
      setCatalog(cat.data.widgets || []);
      setCategories(cat.data.categories || []);
      setLayout(lay.data.widgets || []);
      setPreferences(lay.data.preferences || {});
      setLoading(false);
      await fetchData((lay.data.widgets || []).map((w) => w.widget_key));
    } catch {
      setLoading(false);
      toast.error('Could not load your dashboard');
    }
  }, [fetchData]);

  useEffect(() => { load(); }, [load]);

  // Per-widget refresh intervals, batched into one request.
  useEffect(() => {
    const tick = setInterval(() => {
      if (editMode) return;
      const now = Date.now();
      const due = layout
        .map((w) => byKey[w.widget_key])
        .filter((d) => d?.refresh_interval)
        .filter((d) => now - (fetchedAt.current[d.widget_key] || 0) >= d.refresh_interval * 1000)
        .map((d) => d.widget_key);
      if (due.length) fetchData(due, true);
    }, 60000);
    return () => clearInterval(tick);
  }, [layout, byKey, editMode, fetchData]);

  // ── layout editing ──
  const startEdit = () => setDraft(layout.map((w) => ({ ...w })));
  const cancelEdit = () => setDraft(null);

  const commit = async () => {
    setSaving(true);
    try {
      const res = await dashboardAPI.saveLayout(draft);
      const saved = res.data.widgets || [];
      setLayout(saved);
      setDraft(null);
      const missing = saved.map((w) => w.widget_key).filter((k) => !payloads[k]);
      if (missing.length) await fetchData(missing);
      toast.success('Dashboard saved');
    } catch {
      toast.error('Could not save your layout');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    try {
      const res = await dashboardAPI.resetLayout();
      const rows = res.data.widgets || [];
      setLayout(rows);
      setDraft(null);
      await fetchData(rows.map((w) => w.widget_key));
      toast.success('Reset to your role default');
    } catch {
      toast.error('Could not reset your layout');
    }
  };

  const move = (index, delta) => setDraft((rows) => {
    const next = [...rows];
    const to = index + delta;
    if (to < 0 || to >= next.length) return rows;
    [next[index], next[to]] = [next[to], next[index]];
    return next.map((w, i) => ({ ...w, position: i }));
  });

  const resize = (index, delta) => setDraft((rows) => rows.map((w, i) => {
    if (i !== index) return w;
    const def = byKey[w.widget_key] || {};
    const lo = SIZE_ORDER.indexOf(def.min_size || 'small');
    const hi = SIZE_ORDER.indexOf(def.max_size || 'large');
    const at = SIZE_ORDER.indexOf(w.size);
    const to = Math.min(hi, Math.max(lo, at + delta));
    return { ...w, size: SIZE_ORDER[to] };
  }));

  const remove = (index) =>
    setDraft((rows) => rows.filter((_, i) => i !== index).map((w, i) => ({ ...w, position: i })));

  const addWidget = (key) => {
    const def = byKey[key];
    if (!def) return;
    const row = { widget_key: key, size: def.default_size, collapsed: false, position: 0 };
    if (editMode) {
      setDraft((rows) => [...rows, { ...row, position: rows.length }]);
    } else {
      const next = [...layout, { ...row, position: layout.length }];
      setLayout(next);
      dashboardAPI.saveLayout(next).catch(() => toast.error('Could not add that widget'));
      if (!payloads[key]) fetchData([key]);
    }
    setPickerOpen(false);
  };

  const toggleCollapse = (index) => {
    const rows = view.map((w, i) => (i === index ? { ...w, collapsed: !w.collapsed } : w));
    if (editMode) setDraft(rows);
    else {
      setLayout(rows);
      dashboardAPI.saveLayout(rows).catch(() => { /* cosmetic only */ });
    }
  };

  const onDrop = (index) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    if (from === null || from === index) return;
    setDraft((rows) => {
      const next = [...rows];
      const [moved] = next.splice(from, 1);
      next.splice(index, 0, moved);
      return next.map((w, i) => ({ ...w, position: i }));
    });
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="h-9 w-64 skeleton-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-40 skeleton-pulse rounded-lg md:col-span-2" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Greeting — small, human, and it confirms which role's view you're on */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-fg">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'},{' '}
            {user?.first_name || 'there'}
          </h1>
          <div className="text-sm text-fg-muted mt-1 flex items-center gap-2 flex-wrap">
            <span>{today()}</span>
            <span aria-hidden="true">·</span>
            <Badge variant="outline" className="text-xs font-normal" data-testid="dashboard-role-badge">
              {roleLabel(user?.role)}
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}
                data-testid="dashboard-add-widget">
                <Plus className="w-4 h-4 mr-1.5" />Add widget
              </Button>
              <Button variant="ghost" size="sm" onClick={reset} data-testid="dashboard-reset">
                <RotateCcw className="w-4 h-4 mr-1.5" />Reset to default
              </Button>
              <Button variant="ghost" size="sm" onClick={cancelEdit} data-testid="dashboard-cancel-edit">
                <X className="w-4 h-4 mr-1.5" />Cancel
              </Button>
              <Button size="sm" onClick={commit} disabled={saving} data-testid="dashboard-save-layout">
                <Save className="w-4 h-4 mr-1.5" />Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm"
                onClick={() => fetchData(layout.map((w) => w.widget_key), true)}
                data-testid="dashboard-refresh">
                <RefreshCw className="w-4 h-4 mr-1.5" />Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={startEdit}
                data-testid="dashboard-edit-layout">
                <Pencil className="w-4 h-4 mr-1.5" />Customize
              </Button>
            </>
          )}
        </div>
      </div>

      {editMode && (
        <p className="text-xs text-fg-muted" data-testid="dashboard-edit-hint">
          Drag a card by its handle to move it, or use the ← → arrows to nudge it and the
          size buttons to make it wider or narrower. Nothing is saved until you press Save.
        </p>
      )}

      <CredentialSummaryCard />

      {view.length === 0 ? (
        <div className="stat-card text-center py-14" data-testid="dashboard-empty">
          <LayoutGrid className="w-8 h-8 mx-auto text-fg-muted mb-3" />
          <p className="text-base font-semibold text-fg">Your dashboard is empty</p>
          <p className="text-sm text-fg-muted mt-1 mb-4">
            Pick the widgets you want to see every morning.
          </p>
          <Button onClick={() => { if (!editMode) startEdit(); setPickerOpen(true); }}
            data-testid="dashboard-empty-add">
            <Plus className="w-4 h-4 mr-1.5" />Add your first widget
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-start"
          data-testid="dashboard-grid">
          {view.map((row, index) => {
            const def = byKey[row.widget_key];
            if (!def) return null;
            const Body = WIDGET_COMPONENTS[row.widget_key];
            const payload = payloads[row.widget_key];
            const moduleTo = WIDGET_MODULE_LINKS[row.widget_key];
            return (
              <WidgetCard
                key={row.widget_key}
                definition={def}
                layout={row}
                payload={payload}
                badge={badgeFor(row.widget_key, payload?.data)}
                loading={busyKeys.has(row.widget_key) && !payload}
                editMode={editMode}
                moduleLink={moduleTo ? () => navigate(moduleTo) : null}
                onMove={(d) => move(index, d)}
                onResize={(d) => resize(index, d)}
                onRemove={() => remove(index)}
                onToggleCollapse={() => toggleCollapse(index)}
                onDragStart={() => { dragFrom.current = index; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(index)}
              >
                {Body && (
                  <Body
                    data={payload?.data}
                    preferences={preferences}
                    onRefresh={async () => {
                      const lay = await dashboardAPI.getLayout();
                      setPreferences(lay.data.preferences || {});
                      fetchData([row.widget_key], true);
                    }}
                  />
                )}
              </WidgetCard>
            );
          })}
        </div>
      )}

      <WidgetPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        catalog={catalog}
        categories={categories}
        placedKeys={view.map((w) => w.widget_key)}
        onAdd={addWidget}
      />
    </div>
  );
}
